/** @jsx jsx */
import { css, jsx } from '@emotion/react';

import { simplifyQueryValue } from 'js/lib/queryUtils';
import { useLocation, useParams, useRouter, useRoutes } from 'js/lib/useRouter';

import { RouteNames } from 'pages/teach-course/constants';

import { Button, Tag } from '@coursera/cds-core';
import { ArrowNextIcon } from '@coursera/cds-icons';

import LoadingSpinner from 'bundles/grading/components/common/LoadingSpinner';
import useDefaultSubmitterQuery from 'bundles/grading/hooks/useDefaultSubmitterQuery';
import { ReentryLogManager } from 'bundles/grading/utils/ReentryLogManager';

import _t from 'i18n!nls/grading';

const styles = {
  tag: css`
    margin-bottom: var(--cds-spacing-100);
  `,
  button: css`
    width: 100%;
  `,
};

type Props = {
  showNewFeatureTag?: boolean;
  label?: string;
};

/**
 * Renders "Start Grading" button on Assignment Grading page
 * (list of SGA submissions)
 * @param Props
 * @returns React.JSX.Element
 */
const EntryPointButton = ({ label, showNewFeatureTag }: Props) => {
  const params = useParams();
  const location = useLocation();
  const routes = useRoutes();
  const router = useRouter();

  const { courseSlug, contextId, itemId } = params;
  const gradingGroupId = location?.query?.groupId;

  // check if any reentry log ids exist as we should skip the DefaultSubmitter
  // query and use cached submittedId from the ReentryLogManager
  ReentryLogManager.removeStaleReentryLogItems();
  const reentryLog = new ReentryLogManager(contextId, itemId, simplifyQueryValue(gradingGroupId));
  const reentryLogIds = reentryLog.getPreviousSessionFromLocalStorage();

  const { defaultSubmitterId, loading } = useDefaultSubmitterQuery({ skip: !!reentryLogIds?.submitterId });

  const submitterId = reentryLogIds?.submitterId || defaultSubmitterId;

  const reentryRouterQuery = reentryLogIds
    ? {
        partId: reentryLogIds?.partId,
        rubricId: reentryLogIds?.rubricId,
        'last-part': reentryLogIds?.lastPart,
      }
    : {};

  const buttonDisabled =
    loading || !submitterId || routes[routes.length - 1].name === RouteNames.ASSIGNMENT_QUICK_GRADER;
  return (
    <div>
      {showNewFeatureTag && (
        <div>
          <Tag variant="category" priority="primary" color="highlight2" css={styles.tag}>
            {_t('New feature')}
          </Tag>
        </div>
      )}
      <Button
        onClick={() => {
          if (!buttonDisabled) {
            router.push({
              name: RouteNames.ASSIGNMENT_QUICK_GRADER,
              params: {
                courseSlug,
                contextId,
                itemId,
                ...(submitterId && { id: submitterId }),
              },
              query: {
                ...(gradingGroupId && { groupId: gradingGroupId }),
                ...reentryRouterQuery,
              },
            });
          }
        }}
        icon={loading ? <LoadingSpinner /> : <ArrowNextIcon />}
        css={styles.button}
        disabled={!!buttonDisabled}
        data-pendo-guide="quick-grader-entry-point"
      >
        {label || _t('Start Grading')}
      </Button>
    </div>
  );
};

export default EntryPointButton;
